import React, { useContext, useEffect, useState } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { BaseURL } from "../../../../Files/BaseURL";
import axios from "axios";
import AlertContext from "../../../../Context/Alert/AlertContext";
import ThemeContext from "../../../../Context/theme/ThemeContext";

const MarketPlaceDetail = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  const [formData, setFormData] = useState({
    Title: "",
    Category: "",
    SubCategory: "",
    Keywords: [],
    Images: [],
    Pacakage: {
      Title: "",
      Description: "",
      Delivery: "",
      Concept: "",
      Price: "",
    },
  });

  const fetchPortfolio = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BaseURL}/api/portfolio/marketplace/fetchmarket/${id}`,
        {
          headers: {
            "auth-token": localStorage.getItem("p@token"),
          },
        }
      );
      const portfolioData = response.data;
      setFormData((prevState) => ({
        ...prevState,
        Images: portfolioData.Images.map((item) => ({
          file: null,
          preview: item.img,
        })),
        Pacakage: {
          Title: portfolioData.Pacakage.Title || "",
          Description: portfolioData.Pacakage.Description || "",
          Delivery: portfolioData.Pacakage.Delivery || "",
          Concept: portfolioData.Pacakage.Concept || "",
          Price: portfolioData.Pacakage.Price || "",
        },
      }));
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error fetching portfolio data",
        "danger"
      );
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    try {
      const { formData: stateFormData } = location.state || {};
      setFormData((prev) => ({
        ...prev,
        Title: stateFormData?.title || "",
        Category: stateFormData?.category || "",
        SubCategory: stateFormData?.subcategory || "",
        Keywords: stateFormData?.keywords || [],
      }));

      const query = new URLSearchParams(location.search);
      const editId = query.get("editid");
      if (editId) {
        fetchPortfolio(editId);
      } else {
        setIsInitializing(false);
      }
    } catch (error) {
      showAlert("Error initializing form", "danger");
      setIsInitializing(false);
    }
  }, [location.state, location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      Pacakage: {
        ...prev.Pacakage,
        [name]: value,
      },
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Validate file types
    const validFiles = files.filter((file) => {
      const isValid = file.type.startsWith("image/");
      if (!isValid) {
        showAlert("Please upload only image files", "warning");
      }
      return isValid;
    });

    // Validate file size (5MB max)
    const validSizedFiles = validFiles.filter((file) => {
      const isValidSize = file.size <= 5 * 1024 * 1024;
      if (!isValidSize) {
        showAlert("Each file must be less than 5MB", "warning");
      }
      return isValidSize;
    });

    const imageFiles = validSizedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    // Check if adding new images would exceed the limit
    if (formData.Images.length + imageFiles.length > 3) {
      showAlert("Maximum 3 images allowed", "warning");
      // Only add images up to the limit
      const remainingSlots = 3 - formData.Images.length;
      const limitedImageFiles = imageFiles.slice(0, remainingSlots);

      setFormData((prev) => ({
        ...prev,
        Images: [...prev.Images, ...limitedImageFiles],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      Images: [...prev.Images, ...imageFiles],
    }));
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      Images: prev.Images.filter((_, idx) => idx !== index),
    }));
  };

  const validateForm = () => {
    const { Pacakage, Images } = formData;

    if (!Pacakage.Title.trim()) {
      showAlert("Please enter a package title", "warning");
      return false;
    }
    if (!Pacakage.Description.trim()) {
      showAlert("Please enter a description", "warning");
      return false;
    }
    if (!Pacakage.Delivery.trim()) {
      showAlert("Please enter delivery time", "warning");
      return false;
    }
    if (!Pacakage.Concept || Pacakage.Concept <= 0) {
      showAlert("Please enter a valid number of concepts", "warning");
      return false;
    }
    if (!Pacakage.Price || Pacakage.Price <= 0) {
      showAlert("Please enter a valid price", "warning");
      return false;
    }
    if (Images.length === 0) {
      showAlert("Please upload at least one image", "warning");
      return false;
    }

    return true;
  };

  const handleNextClick = async () => {
    try {
      if (!validateForm()) return;

      setIsLoading(true);
      const query = new URLSearchParams(location.search);
      const editId = query.get("editid");

      if (editId) {
        navigate(
          `/user-dashboard/profile/marketplace_overview?editid=${editId}`,
          {
            state: { formData },
          }
        );
      } else {
        navigate("/user-dashboard/profile/marketplace_overview", {
          state: { formData },
        });
      }
    } catch (error) {
      showAlert("Navigation failed", "danger");
      setIsLoading(false);
    }
  };

  // Clean up URLs on unmount
  useEffect(() => {
    return () => {
      // Clean up the URL objects
      formData.Images.forEach((image) => {
        if (image.preview && !image.preview.startsWith("http")) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  if (isInitializing) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primarygreen"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center font-pop flex-col">
      <div
        className={`rounded-2xl py-6 px-6 my-6 w-[95%] md:w-[70%] xl:w-[40%] flex flex-col gap-4
            ${theme === "dark" ? "bg-black text-white" : "bg-white"}`}
      >
        <h1 className="font-semibold text-xl text-center">Request a Job</h1>
        <div className="flex flex-col gap-4">
          <input
            type="text"
            name="Title"
            value={formData.Pacakage.Title}
            onChange={handleInputChange}
            placeholder="Title of package"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none
                        ${
                          theme === "dark"
                            ? "bg-black text-white border border-white"
                            : "bg-[#E8E8E8]"
                        }`}
          />
          <textarea
            name="Description"
            value={formData.Pacakage.Description}
            onChange={handleInputChange}
            placeholder="Describe details of your offering"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-lg bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none
                            ${
                              theme === "dark"
                                ? "bg-black text-white border border-white"
                                : "bg-[#E8E8E8]"
                            }`}
          />
          <input
            type="text"
            name="Delivery"
            value={formData.Pacakage.Delivery}
            onChange={handleInputChange}
            placeholder="Delivery time"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none
                            ${
                              theme === "dark"
                                ? "bg-black text-white border border-white"
                                : "bg-[#E8E8E8]"
                            }`}
          />
          <input
            type="number"
            name="Concept"
            value={formData.Pacakage.Concept}
            onChange={handleInputChange}
            placeholder="Number of concepts included"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none
                            ${
                              theme === "dark"
                                ? "bg-black text-white border border-white"
                                : "bg-[#E8E8E8]"
                            }`}
          />
          <input
            type="number"
            name="Price"
            value={formData.Pacakage.Price}
            onChange={handleInputChange}
            placeholder="Price"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none
                            ${
                              theme === "dark"
                                ? "bg-black text-white border border-white"
                                : "bg-[#E8E8E8]"
                            }`}
          />
          <div className="flex items-center justify-center h-44 w-full">
            <label
              htmlFor="dropzone-file"
              className={`flex flex-col h-full items-center justify-center w-full border-2 border-primarygreen border-dashed rounded-lg cursor-pointer
                        ${
                          theme === "dark" ? "bg-black text-white" : "bg-white"
                        }`}
            >
              <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                <IoIosAddCircleOutline className="text-2xl" />
                <p className={`text-base ${theme === "dark" && "text-white"}`}>
                  Attach file
                </p>
              </div>
              <input
                id="dropzone-file"
                accept="image/*"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                multiple
              />
            </label>
          </div>
          <div className="flex gap-4">
            {formData.Images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image.preview}
                  alt={`Preview ${index}`}
                  className="rounded-lg max-h-40"
                />
                <button
                  className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md"
                  onClick={() => removeImage(index)}
                >
                  <svg
                    className="h-6 w-6 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            className={`my-4 px-2 md:px-6 bg-green-500 hover:bg-transparent hover:bg-white-500 font-medium hover:text-black font-pop text-sm md:text-lg py-2 md:py-3 border border-black-500 hover:border-green-500 rounded-full text-white
                            ${
                              theme === "dark"
                                ? "hover:text-white"
                                : "hover:text-black"
                            }
                            ${
                              isLoading
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
            onClick={handleNextClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </div>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketPlaceDetail;
